import { ReactElement, useEffect, useRef, useState } from 'react';
import { Box } from '@twilio-paste/core/box';
import { cloneDeep } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  NewAiMessageStatus,
  SendMessageFormValues,
  WiseOwlChat,
  WiseOwlProvider,
  feedbackTypes,
  spliceBlocks,
  findLastFailedUserMessage,
  WiseOwlLoadingConfiguration,
  createNewUserMessageBlock,
  setUserMessageBlockFailed,
} from '@twiliointernal/org/twilio/wise-owl/shared/ui';

import {
  MessageBlock,
  WiseOwlApiIngress,
  WiseOwlBlock,
  WiseOwlGetChatSummaryByIdResponseData,
  WiseOwlIngress,
  WiseOwlSyncDocumentContent,
  wiseOwlViewCopy,
  trackingDetails,
} from '@twiliointernal/org/twilio/e2-commons/shared/definitions';
import { useTwilioSyncDocument } from '@twiliointernal/org/twilio/e2-commons/shared/hooks';

import { constants } from './definitions';
import { LegalDisclaimer } from './sub/legalDisclaimer';
import { SupportApi } from '../../clients';
import {
  useWiseOwlCreateChat,
  useWiseOwlSendMessage,
  useWiseOwlSubmitFeedback,
  useUserDetails,
  useSegment,
  useWiseOwlGetChatSummary,
} from '../../hooks';
import logger from '../../../lib/logger';
import { loginRedirectToOsc } from '../../utils';

const WiseOwlView = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = useRef(location.pathname);
  const { chatId: chatIdFromRoute } = useParams<'chatId'>() as {
    chatId?: string;
  };
  const { user, isLoggedIn } = useUserDetails();
  const chatId = useRef<string | undefined>(chatIdFromRoute);
  const { trackEvent } = useSegment();
  const [loadingConfiguration, updateLoadingConfiguration] =
    useState<WiseOwlLoadingConfiguration>(constants.defaultLoadingState);
  const shouldFetchChat = !!(
    chatIdFromRoute &&
    isLoggedIn &&
    !loadingConfiguration.submission
  );
  const [newAiMessageStatus, setNewAiMessageStatus] =
    useState<NewAiMessageStatus>(NewAiMessageStatus.None);
  const [blocks, setBlocks] = useState<WiseOwlBlock[]>([]);
  const previousBlocks = useRef<WiseOwlBlock[]>([]);
  const [failedUserMessageId, setFailedUserMessageId] = useState<string>();
  const [disableChatMessageSubmission, setChatSubmissionDisabled] =
    useState<boolean>();

  const { mutateAsync: createChatMutation } = useWiseOwlCreateChat({
    isLoggedIn,
  });
  const { mutateAsync: sendMessageMutation } = useWiseOwlSendMessage({
    isLoggedIn,
  });
  const { mutateAsync: createFeedbackMutation } = useWiseOwlSubmitFeedback({
    isLoggedIn,
  });
  const { mutateAsync: getChatSummaryMutation } = useWiseOwlGetChatSummary({
    isLoggedIn,
  });
  const ingress = WiseOwlApiIngress.OneSupportCenter;

  useEffect(() => {
    const isMovingToLanding =
      location.pathname === '/' && currentPath.current !== '/';
    const isMovingToChatRoute =
      location.pathname.includes('chat') &&
      !currentPath.current.includes('chat');

    // Reset WO state when moving to Landing
    if (isMovingToLanding) {
      chatId.current = undefined;
      setBlocks([]);
    }

    // Fetch chat by ID when moving to /chat route match
    if (isMovingToChatRoute && shouldFetchChat) {
      chatId.current = chatIdFromRoute;
      handleGetChat(chatIdFromRoute);
    }

    // Update currentPath ref when changing
    currentPath.current = location.pathname;
  }, [location]);

  useEffect(() => {
    // User is logged in and chatId is present in route
    if (shouldFetchChat) {
      handleGetChat(chatIdFromRoute);
    }

    /**
     * User is not logged in and chatId is present in route => redirect
     * This is because we match a chatId by a userId in the WO API, which
     * cannot be done for ungated users because the stargateUngatedUserId
     * can be inconsistent.
     */
    if (!isLoggedIn && chatIdFromRoute) {
      navigate('/');
    }

    // Cleanup chatId/blocks when navigating away from WOSC
    return () => {
      chatId.current = undefined;
      setBlocks([]);
      updateLoadingConfiguration(constants.defaultLoadingState);
    };
  }, [isLoggedIn]);

  // Locates any failed user Message, updates "success" value to false, and inserts newly updated block into state.
  useEffect(() => {
    if (blocks.length && failedUserMessageId) {
      // Identify index of failed message
      const { failedMessageIndex, failedMessage } = findLastFailedUserMessage(
        blocks,
        failedUserMessageId,
      );

      // Set success value to false in cloned block
      const updatedBlock = setUserMessageBlockFailed(failedMessage);

      // Replaces original failed message block with updated block.
      setBlocks((currentBlocks) =>
        spliceBlocks(currentBlocks, failedMessageIndex, updatedBlock),
      );
    }
  }, [failedUserMessageId]);

  const handleGetChat = async (chatIdToFetch: string): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: true,
      });

      const response = await SupportApi.getWiseOwlChatsById(chatIdToFetch);

      logger.info(`${constants.LOG_PREFIX}::Fetched chat`, {
        chatId: chatIdToFetch,
      });

      const { blocks } = response.data;

      setBlocks(blocks);
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });
    } catch (err) {
      logger.error(`${constants.LOG_PREFIX}::Error fetching chat`, {
        chatId: chatIdToFetch,
      });

      /**
       * TODO: Proper messaging/handling on chatId fetch error
       * It's likely an error occurs here because the chatId is not found
       * or doesn't belong to the current user. In this case, we reset the
       * chatId and navigate to the home page under the hood
       */
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        fetchChatById: false,
      });
      chatId.current = undefined;
      navigate('/');
    }
  };

  const handleOnInitialize = (
    documentData: WiseOwlSyncDocumentContent,
  ): void => {
    const { blocks: newBlocks } = documentData;

    logger.info(`${constants.LOG_PREFIX}::Document sync initialized`, {
      chatId: chatId.current,
    });

    setNewAiMessageStatus(NewAiMessageStatus.Loading);

    /**
     * The document will have some initial content to render on sync
     * instantiation so we want to render that before the stream starts
     */

    setBlocks((currentBlocks) => currentBlocks.concat(newBlocks));
  };

  const handleOnStream = (documentData: WiseOwlSyncDocumentContent): void => {
    const { blocks: newBlocks } = documentData;

    setBlocks(previousBlocks.current.concat(newBlocks));
  };

  const handleOnComplete = (documentData: WiseOwlSyncDocumentContent): void => {
    const { blocks: newBlocks } = documentData;

    logger.info(`${constants.LOG_PREFIX}::Document sync completed`, {
      chatId: chatId.current,
    });

    /**
     * Here, we update the ref with the updatedBlocks and add any new
     * blocks returned from the stream at the end
     */
    setBlocks(previousBlocks.current.concat(newBlocks));
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
    setNewAiMessageStatus(NewAiMessageStatus.Complete);
  };

  const handleOnError = (): void => {
    logger.error(`${constants.LOG_PREFIX}::Error streaming document data`, {
      chatId: chatId.current,
    });
    updateLoadingConfiguration({
      ...constants.defaultLoadingState,
      submission: false,
    });
    setNewAiMessageStatus(NewAiMessageStatus.None);
  };

  const { handleDocumentSync } =
    useTwilioSyncDocument<WiseOwlSyncDocumentContent>({
      onComplete: handleOnComplete,
      onError: handleOnError,
      onInitialize: handleOnInitialize,
      onStream: handleOnStream,
    });

  // consider moving to helpers
  const generateNewUserMessage = (
    input: string,
    createChatOnRetry: boolean,
  ): MessageBlock => {
    const newUserMessage: MessageBlock = createNewUserMessageBlock(
      input,
      createChatOnRetry,
    );

    setBlocks((currentBlocks) => {
      /**
       * Deep-copy blocks up to this point. Streaming will write blocks on-top of this
       * state.
x     */
      const newBlocks = cloneDeep(currentBlocks).concat(newUserMessage);

      /**
       * previousBlocks is officially "preStreamingBlocks". We 'freeze' our existing block state,
       * and then we're safe to write all incoming block(s) from a message's SyncDoc.
       *
       * see handleOnStream to see how we're doing this.
       */
      previousBlocks.current = newBlocks;
      return newBlocks;
    });
    return newUserMessage;
  };

  const handleStartNewChat = async (
    data: SendMessageFormValues,
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
      });

      const createChatOnRetry = true;
      const { input } = data;
      const newUserMessage = generateNewUserMessage(input, createChatOnRetry);

      const { success, data: response } = await createChatMutation(input);
      const { accessToken, syncDocumentSid, chatId: newChatId } = response;
      const {
        values: { retryId },
      } = newUserMessage;

      if (!success) {
        setFailedUserMessageId(retryId);
        setChatSubmissionDisabled(true);
        throw new Error('Chat creation failed');
      }

      logger.info(
        `${constants.LOG_PREFIX}::Creating ${
          isLoggedIn ? 'gated' : 'ungated'
        } Wise Owl chat`,
        {
          chatId: newChatId,
          syncDocumentSid,
        },
      );

      setChatSubmissionDisabled(false);
      chatId.current = newChatId;
      navigate(`/chat/${newChatId}`);
      handleDocumentSync({
        accessToken,
        syncDocumentSid,
      });
    } catch {
      logger.error(`${constants.LOG_PREFIX}::Chat creation failed`);
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: false,
      });
    }
  };

  const handleSendChatMessage = async (
    data: SendMessageFormValues,
  ): Promise<void> => {
    try {
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: true,
      });

      const createChatOnRetry = false;
      const newUserMessage = generateNewUserMessage(
        data.input,
        createChatOnRetry,
      );
      const {
        values: { retryId },
      } = newUserMessage;

      if (data.chatId) {
        const { success, data: response } = await sendMessageMutation({
          chatId: data.chatId,
          input: data.input,
        });

        if (!success) {
          setFailedUserMessageId(retryId);
          throw new Error('Message send failed');
        }

        const { accessToken, syncDocumentSid } = response;

        logger.info(
          `${constants.LOG_PREFIX}::Updating ${
            isLoggedIn ? 'gated' : 'ungated'
          } Wise Owl chat`,
          {
            chatId: data.chatId,
            syncDocumentSid,
          },
        );

        handleDocumentSync({
          accessToken,
          syncDocumentSid,
        });
      }
    } catch {
      logger.error(`${constants.LOG_PREFIX}::Message send failed`, {
        chatId: data.chatId,
      });
      updateLoadingConfiguration({
        ...constants.defaultLoadingState,
        submission: false,
      });
    }
  };

  const handleSubmitFeedback = async (
    data: feedbackTypes.CustomerFeedbackFormValues,
  ): Promise<void> => {
    try {
      const { messageId, feedback, positive } = data;

      if (chatId.current) {
        const { success } = await createFeedbackMutation({
          chatId: chatId.current,
          messageId,
          feedback,
          positive,
        });

        if (!success) {
          throw new Error('Create feedback failed');
        }

        logger.info(
          `${constants.LOG_PREFIX}::Creating ${
            isLoggedIn ? 'gated' : 'ungated'
          } message feedback`,
          {
            chatId: chatId.current,
            messageId,
            positive: positive ? 'positive' : 'negative',
          },
        );
      }
    } catch (err) {
      logger.error(`${constants.LOG_PREFIX}::Create feedback failed`, {
        chatId: chatId.current,
      });
      throw err;
    }
  };

  const handleRedirectAction = (redirectUrl: string): void => {
    navigate(`/${redirectUrl}`);
  };

  const handleMessageRetry = (retryId: string): void => {
    const { failedMessageIndex, failedMessage } = findLastFailedUserMessage(
      blocks,
      retryId,
    );

    setBlocks((currentBlocks) =>
      spliceBlocks(currentBlocks, failedMessageIndex),
    );

    if (failedMessage.values.createChatOnRetry) {
      handleStartNewChat({ input: failedMessage.values.message });
    } else {
      handleSendChatMessage({
        input: failedMessage.values.message,
        chatId: chatId.current,
      });
    }
  };

  const handleLoginRedirect = () => {
    loginRedirectToOsc();
    trackEvent(trackingDetails.wiseOwlFeedbackLogin, { user, ingress });
  };

  const handleTrackEvent = (
    eventName: string,
    additionalData: Record<string, unknown>,
  ): void => {
    trackEvent(eventName, { user, ingress, ...additionalData });
  };

  const handleGetChatSummary = async (): Promise<
    WiseOwlGetChatSummaryByIdResponseData | undefined
  > => {
    try {
      if (chatId.current) {
        const { success, data: response } = await getChatSummaryMutation(
          chatId.current,
        );

        const { subject, description, wasTranslated } = response;

        if (!success) {
          throw new Error('Chat summary failed');
        }

        logger.info(
          `${constants.LOG_PREFIX}::Getting ${
            isLoggedIn ? 'gated' : 'ungated'
          } Wise Owl chat summary`,
          {
            chatId: chatId.current,
          },
        );

        return { subject, description, wasTranslated };
      }
    } catch {
      logger.error(`${constants.LOG_PREFIX}::Chat summary failed`);
    }
  };

  const preChatProps = {
    hasImage: true,
    headerText: wiseOwlViewCopy.preChat.headerText,
    loading: loadingConfiguration.preChat,
    subText: wiseOwlViewCopy.preChat.subText,
    blocks: constants.preChat.recommendedPrompts,
    anchorProps: {
      children: wiseOwlViewCopy.preChat.anchorText,
      href: constants.preChat.href,
      showExternal: true,
    },
  };

  // init WO Context
  const context = {
    ingress: user ? WiseOwlIngress.osc : WiseOwlIngress.oscUngated,
    chatId: chatId.current,
    loadingConfiguration,
    isLoggedIn,
    author: user ? `${user.firstName} ${user.lastName}` : undefined,
    handlers: {
      handleSendChatMessage,
      handleStartNewChat,
      handleSubmitFeedback,
      handleRedirectAction,
      handleLoginRedirect,
      handleTrackEvent,
      handleMessageRetry,
      handleGetChatSummary,
    },
  };

  return (
    <WiseOwlProvider context={context}>
      <Box
        display="flex"
        flexDirection="column"
        marginTop="space90"
        height={constants.WISEOWL_CHAT_HEIGHT}
        minHeight={constants.WISEOWL_CHAT_MIN_HEIGHT}
      >
        <WiseOwlChat
          loadingConfiguration={loadingConfiguration}
          chatId={chatId.current}
          chatHistory={[]}
          blocks={blocks}
          newAiMessageStatus={newAiMessageStatus}
          preChatProps={preChatProps}
          onStartNewChat={handleStartNewChat}
          onSendChatMessage={handleSendChatMessage}
          onSubmitFeedback={handleSubmitFeedback}
          handleRetry={handleMessageRetry}
          disabled={disableChatMessageSubmission}
        />
      </Box>
      <LegalDisclaimer />
    </WiseOwlProvider>
  );
};

export default WiseOwlView;
